/**
 * MIRROR de backend/src/constants/appointmentStatuses.ts.
 * Manter sincronizado: ao alterar o catálogo do backend, atualizar este arquivo.
 *
 * Catálogo de status de agendamento por `BusinessSegment` usado para
 * renderizar os botões de transição de status no calendário do operador.
 *
 * O backend valida `status × segmento` em `PATCH /api/appointments/:id/status`
 * — este arquivo apenas espelha o catálogo para a UI; nunca deve ser
 * fonte da verdade de regras de negócio.
 *
 * Cada entrada contém:
 *  - `value`: identificador interno persistido em `Appointment.status` (mesmo
 *    valor enviado pelo backend e aceito pela API).
 *  - `label`: rótulo final em pt-BR pronto para renderização (emoji + texto).
 */

/** @typedef {{ value: string, label: string }} AppointmentStatusOption */

/**
 * Catálogo de status válidos por segmento. Chaves espelham o enum
 * `BusinessSegment` do Prisma no backend.
 *
 * @type {Record<"barbearias" | "consultorios" | "mecanicas" | "lava_jatos" | "academias", AppointmentStatusOption[]>}
 */
export const STATUS_BY_SEGMENT = {
  barbearias: [
    { value: "confirmed", label: "✅ Confirmado" },
    { value: "em_atendimento", label: "💈 Em atendimento" },
    { value: "finalizado", label: "🎉 Finalizado" },
    { value: "cancelled", label: "🚫 Cancelado" },
  ],
  consultorios: [
    { value: "confirmed", label: "✅ Confirmado" },
    { value: "em_atendimento", label: "🩺 Em atendimento" },
    { value: "finalizado", label: "🎉 Finalizado" },
    { value: "cancelled", label: "🚫 Cancelado" },
  ],
  mecanicas: [
    { value: "confirmed", label: "✅ Confirmado" },
    { value: "aguardando_peca", label: "🔧 Aguardando peça" },
    { value: "em_execucao", label: "⚙️ Em execução" },
    { value: "pronto", label: "🎉 Pronto!" },
    { value: "cancelled", label: "🚫 Cancelado" },
  ],
  lava_jatos: [
    { value: "confirmed", label: "✅ Confirmado" },
    { value: "em_execucao", label: "🚿 Em execução" },
    { value: "pronto", label: "🎉 Pronto!" },
    { value: "cancelled", label: "🚫 Cancelado" },
  ],
  academias: [
    { value: "confirmed", label: "✅ Confirmado" },
    { value: "em_atendimento", label: "🏋️ Em atendimento" },
    { value: "finalizado", label: "🎉 Finalizado" },
    { value: "cancelled", label: "🚫 Cancelado" },
  ],
};

/**
 * Fallback seguro quando o segmento do tenant é desconhecido (`null`/`undefined`
 * ou valor fora do catálogo). Permite apenas confirmar/cancelar para não
 * bloquear o fluxo essencial — espelha o `FALLBACK_STATUSES` do backend.
 *
 * @type {AppointmentStatusOption[]}
 */
const FALLBACK_STATUSES = [
  { value: "confirmed", label: "✅ Confirmado" },
  { value: "cancelled", label: "🚫 Cancelado" },
];

/**
 * Retorna o catálogo de status válidos para o segmento informado.
 *
 * Quando `segment` é `null`/`undefined` ou não está no catálogo, retorna
 * o fallback `[confirmed, cancelled]` — nunca expõe status de outros segmentos.
 *
 * @param {string | null | undefined} segment Segmento do tenant ativo.
 * @returns {AppointmentStatusOption[]} Lista de opções válidas para a UI.
 */
export function getStatusesForSegment(segment) {
  if (!segment) return FALLBACK_STATUSES;
  return STATUS_BY_SEGMENT[segment] ?? FALLBACK_STATUSES;
}
